import React, { useState, useEffect, useRef } from 'react';
import { Progress, Popover, Col, Row, Image, Card, Drawer, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import errorImg from '@/assets/screenshotError.png';
import loadingImg from '@/assets/loading.png';
import { ReportStatus } from './reportState.js';
import { ReportTimeline } from './reportHorizontalTimeline.js';
import './TabPane.css';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getReportCta } from '@/services/report.js';
import { useRequest } from 'ahooks';
import { useOutletContext } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';

const TabPane = props => {
  // start
  const { t } = useTranslation('report');
  const statusScrollRef = useRef(null);

  const details = props.name;

  const isShowImage = details.isUrl === 1 ? true : false;
  const isShowPhone = details.isUrl === 3 ? true : false;
  const isShowCard = details.isUrl === 4 || details.isUrl === 5 ? true : false;
  const isShowUserImage = details.userImageUrl.length === 0 ? false : true;
  const { isMobile } = useOutletContext();
  const { data, runAsync } = useRequest(getReportCta, {
    manual: true,
  });

  useEffect(() => {
    runAsync(details.externalRef);
  }, []);

  const [progressList, setProgressList] = useState([]);
  const [imageSrcList, setImageSrcList] = useState([]);
  const [showImageList, setShowImageList] = useState([]);
  const [hideProgressList, setHideProgressList] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const downloadSingleImage = async (url, index) => {
    try {
      const response = await fetch(url);
      const reader = response.body.getReader();
      const contentLength = +response.headers.get('Content-Length');
      let receivedLength = 0;
      const chunks = [];

      const MAX_ITERATIONS = 10000;
      let iterationCount = 0;
      let isReading = true;

      while (isReading && iterationCount < MAX_ITERATIONS) {
        iterationCount++;
        const { done, value } = await reader.read();
        if (done) {
          isReading = false;
          break;
        }

        chunks.push(value);
        receivedLength += value.length;

        const newProgress = Math.min(99, Math.floor((receivedLength / contentLength) * 100));

        setProgressList(prev => {
          const newList = [...prev];
          newList[index] = newProgress;
          return newList;
        });
      }

      if (iterationCount >= MAX_ITERATIONS) {
        throw new Error('Download loop exceeded maximum iterations');
      }

      const blob = new Blob(chunks);
      const objectURL = URL.createObjectURL(blob);

      setImageSrcList(prev => {
        const newList = [...prev];
        newList[index] = objectURL;
        return newList;
      });

      setProgressList(prev => {
        const newList = [...prev];
        newList[index] = 100;
        return newList;
      });

      setTimeout(() => {
        setHideProgressList(prev => {
          const newList = [...prev];
          newList[index] = true;
          return newList;
        });

        setShowImageList(prev => {
          const newList = [...prev];
          newList[index] = true;
          return newList;
        });
      }, 300);
    } catch (error) {
      console.error(`img ${index + 1} Download failed:`, error);
      setProgressList(prev => {
        const newList = [...prev];
        newList[index] = 0;
        return newList;
      });
      setHideProgressList(prev => {
        const newList = [...prev];
        newList[index] = false;
        return newList;
      });
      setShowImageList(prev => {
        const newList = [...prev];
        newList[index] = false;
        return newList;
      });
    }
  };

  const downloadImages = async urls => {
    if (!urls || !Array.isArray(urls) || urls.length === 0) return;

    const initialProgress = Array(urls.length).fill(0);
    const initialImageSrc = Array(urls.length).fill('');
    const initialShowImage = Array(urls.length).fill(false);
    const initialHideProgress = Array(urls.length).fill(false);

    setProgressList(initialProgress);
    setImageSrcList(initialImageSrc);
    setShowImageList(initialShowImage);
    setHideProgressList(initialHideProgress);
    setCurrentImageIndex(0);

    const downloadPromises = urls.map((url, index) => downloadSingleImage(url, index));

    await Promise.all(downloadPromises);
  };

  useEffect(() => {
    if (data?.userImageUrl) {
      const urls = Array.isArray(data.userImageUrl) ? data.userImageUrl : [data.userImageUrl];
      downloadImages(urls);
    }
  }, [data?.userImageUrl]);

  // Status drawer
  const [open, setOpen] = useState(false);

  const onClose = () => {
    setOpen(false);
  };

  // Image carousel control
  const goToPrevious = () => {
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : imageSrcList.length - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex(prev => (prev < imageSrcList.length - 1 ? prev + 1 : 0));
  };

  const goToImage = index => {
    setCurrentImageIndex(index);
  };

  // Calculate visible module count
  const visibleModules = [
    true, // Details module always shows
    true, // Status module always shows
    isShowUserImage, // User Uploads module
    isShowImage, // Screenshot module
  ].filter(Boolean);

  const visibleModuleCount = visibleModules.length;

  // Calculate each module's span based on visible module count
  const getModuleSpan = () => {
    if (isMobile) {
      return 24; // Mobile always full width
    }

    switch (visibleModuleCount) {
      case 1:
        return 24;
      case 2:
        return 12;
      case 3:
        return 8;
      case 4:
        return 6;
      default:
        return 12;
    }
  };

  const moduleSpan = getModuleSpan();

  return (
    <>
      {/* Main content modules */}
      <Row gutter={[10, 10]} style={{ alignItems: 'stretch' }}>
        {/* Details module */}
        <Col
          xs={24}
          sm={24}
          md={moduleSpan}
          lg={moduleSpan}
          xl={moduleSpan}
          xxl={moduleSpan}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Card
            title={t('report:tabPane.detailsTitle')}
            type="inner"
            data-testid="scams-report-report-details-card"
            style={{ flex: 1, minHeight: '16rem', overflow: 'auto' }}
          >
            <Row>
              <Col span={12}>{t('report:tabPane.details.feedbackTime')} : </Col>
              <Col span={12}>{dayjs(details.feedbackTime).format('YYYY-MM-DD HH:mm:ss')}</Col>
            </Row>
            <Row>
              <Col span={12} style={{ wordBreak: 'break-word' }}>
                {t('report:tabPane.details.firstTime')} :
              </Col>
              <Col span={12}>{dayjs(details.firstTime).format('YYYY-MM-DD HH:mm:ss')}</Col>
            </Row>
            <Row>
              <Col span={12}>{t('report:tabPane.details.userRegion')} :</Col>
              <Col span={12}>{details.userArea}</Col>
            </Row>
            <Row data-testid="scams-report-report-detail-type-row">
              <Col span={12}>{t('report:tabPane.details.type')} :</Col>
              <Col span={12} data-testid="scams-report-report-detail-type-value">
                {details.reportCtaRelaType}
              </Col>
            </Row>
            <Row>
              <Col span={12}>{t('report:tabPane.details.identifyingContent')} :</Col>
              <Col span={12} style={{ wordBreak: 'break-word' }}>
                {details.isUrl === 1 ? <a> {details.identifyContent} </a> : details.identifyContent}
              </Col>
            </Row>
            {details.reportCtaRelaType !== 'BankAccount' && (
              <Row>
                <Col span={12}>
                  {isShowImage
                    ? t('report:tabPane.details.websiteLocation')
                    : t('report:tabPane.details.location')}{' '}
                  :
                </Col>
                <Col span={12}>{isShowImage ? details.websiteArea : details.phoneArea}</Col>
              </Row>
            )}
            {details.reportCtaRelaType !== 'BankAccount' && (
              <Row>
                <Col span={12}>{t('report:tabPane.details.relatedBrands')} :</Col>
                <Col span={12}>{details.relatedBrands}</Col>
              </Row>
            )}
          </Card>
        </Col>

        {/* Status module */}
        <Col
          xs={24}
          sm={24}
          md={moduleSpan}
          lg={moduleSpan}
          xl={moduleSpan}
          xxl={moduleSpan}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Card
            type="inner"
            title={t('report:tabPane.statusTitle')}
            style={{
              flex: 1,
              minHeight: isMobile ? '12rem' : '16rem',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            <div
              className={`status_body_scroll ${isMobile ? 'status_body_scroll_mobile' : ''}`}
              ref={statusScrollRef}
            >
              <Row className={`status_col ${isMobile ? 'status_col_mobile' : ''}`}>
                <Col span={24}>
                  <ReportTimeline name={details.reportCtaRelaState} isMobile={isMobile} />
                </Col>
              </Row>
            </div>
            <div className={`status_description ${isMobile ? 'status_description_mobile' : ''}`}>
              <ReportStatus name={details.reportCtaRelaState} isMobile={isMobile} />
            </div>
          </Card>
        </Col>

        {/* User Uploads module */}
        {isShowUserImage && (
          <Col
            xs={24}
            sm={24}
            md={moduleSpan}
            lg={moduleSpan}
            xl={moduleSpan}
            xxl={moduleSpan}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Card title={t('report:tabPane.userUploads')} type="inner" className="flex-card">
              <div className="carousel-wrapper">
                {/* progress bar */}
                {progressList.length > 0 && progressList[currentImageIndex] < 100 && (
                  <div
                    className="carousel-progress"
                    style={{
                      opacity: hideProgressList[currentImageIndex] ? 0 : 1,
                    }}
                  >
                    <Progress
                      percent={progressList[currentImageIndex]}
                      status="active"
                      showInfo={false}
                      className="carousel-progress-bar"
                    />
                  </div>
                )}

                {/* img show */}
                <div
                  className={`carousel-stage ${isMobile ? 'mobile' : 'desktop'}`}
                  style={{
                    minHeight: isMobile ? '20rem' : '32rem',
                    opacity: showImageList[currentImageIndex] ? 1 : 0,
                  }}
                >
                  {showImageList[currentImageIndex] && imageSrcList[currentImageIndex] && (
                    <Image
                      src={imageSrcList[currentImageIndex]}
                      className={`carousel-image ${isMobile ? 'mobile' : 'desktop'}`}
                    />
                  )}
                </div>

                {/* Left and right arrow navigation */}
                {imageSrcList.length > 1 && showImageList[currentImageIndex] && (
                  <>
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      onClick={goToPrevious}
                      className="carousel-arrow left"
                    />
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={goToNext}
                      className="carousel-arrow right"
                    />
                  </>
                )}

                {/* Bottom indicator point */}
                {imageSrcList.length > 1 && (
                  <div className="carousel-dots">
                    {imageSrcList.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToImage(index)}
                        className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                )}

                {/* Image Counter */}
                {imageSrcList.length > 0 && (
                  <div className="carousel-counter">
                    {currentImageIndex + 1} / {imageSrcList.length}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        )}

        {/* Screenshot module */}
        {isShowImage && (
          <Col
            xs={24}
            sm={24}
            md={moduleSpan}
            lg={moduleSpan}
            xl={moduleSpan}
            xxl={moduleSpan}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Card title={t('report:tabPane.screenshotTitle')} type="inner" className="flex-card">
              <div className="screenshot-wrapper">
                {data?.img == 1 ? (
                  <Image src={data?.imgUrl} style={{ height: isMobile ? '100%' : '32rem' }} />
                ) : data?.img == 3 ? (
                  <Image
                    src={errorImg}
                    preview={false}
                    style={{
                      width: isMobile ? '100%' : '500',
                      height: isMobile ? '100%' : '32rem',
                    }}
                  />
                ) : (
                  <Image
                    src={loadingImg}
                    preview={false}
                    style={{
                      width: isMobile ? '100%' : '500',
                      height: isMobile ? '100%' : '32rem',
                    }}
                  />
                )}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Service provider information module */}
      {isShowImage && (
        /* URL and domain */
        <Row gutter={[10, 10]} style={{ marginTop: '10px' }}>
          <Col span={24}>
            <Card title={t('report:tabPane:serviceProviderTitle')} type="inner">
              <Row>
                <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.serviceProviderTitle')}
                    bordered={false}
                  >
                    {details.networkProvider}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.domainRegistrar')}
                    bordered={false}
                  >
                    {details.domainRegistrar}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.domainResolver')}
                    bordered={false}
                  >
                    {details.domainNameResolver}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Phone and email information module */}
      {isShowPhone && (
        /* Phone && Email */
        <Row gutter={[10, 10]} style={{ marginTop: '10px' }}>
          <Col span={24}>
            <Card title={t('report:tabPane.phoneProviderTitle')} type="inner">
              <Row justify="center">
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.allocatee')}
                    bordered={false}
                  >
                    {details.allocatee}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.allocationDate')}
                    bordered={false}
                  >
                    {details.allocationDate}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.latestHolder')}
                    bordered={false}
                  >
                    {details.latestHolder}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.latestTransferDate')}
                    bordered={false}
                  >
                    {details.latestTransferDate}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={4}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.numberingArea')}
                    bordered={false}
                  >
                    {details.numberingArea}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Bank card information module */}
      {isShowCard && (
        /* bank card */
        <Row gutter={[10, 10]} style={{ marginTop: '10px' }}>
          <Col span={24}>
            <Card title={t('report:tabPane.bankTitle')} type="inner">
              <Row justify="center">
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={6}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.bankName')}
                    bordered={false}
                    extra={
                      <Popover title={t('report:tabPane.bankNameExplain')} trigger="hover">
                        <QuestionCircleOutlined />
                      </Popover>
                    }
                  >
                    {details.bankName}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={6}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.branchName')}
                    bordered={false}
                    extra={
                      <Popover title={t('report:tabPane.branchNameExplain')} trigger="hover">
                        <QuestionCircleOutlined />
                      </Popover>
                    }
                  >
                    {details.bsbName}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={6}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.branchAddress')}
                    bordered={false}
                    extra={
                      <Popover title={t('report:tabPane.branchAddressExplain')} trigger="hover">
                        <QuestionCircleOutlined />
                      </Popover>
                    }
                  >
                    {details.address}
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6} xxl={6}>
                  <Card
                    className="provider_card"
                    title={t('report:tabPane.citySuburb')}
                    bordered={false}
                    extra={
                      <Popover title={t('report:tabPane.citySuburbExplain')} trigger="hover">
                        <QuestionCircleOutlined />
                      </Popover>
                    }
                  >
                    {details.suburb}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Status drawer */}
      <Drawer title={t('report:tabPane.drawerTitle')} onClose={onClose} open={open}>
        <ReportTimeline name={details.reportCtaRelaState} />
      </Drawer>
    </>
  );
};

export default TabPane;
